import useSWR from "swr";

import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { format } from "date-fns";
import { isArray } from "lodash";
import Swal from "sweetalert2";

import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher, fetchApi } from "utils/baseFetcher";

import ToolRegulatoryQuestionnaireFormFieldGroup from "./FieldGroup";

export const regulatoryQuestionnaireFormUrl =
  "/api/v2/ip-regulatory-submissions/form";

export const regulatoryQuestionnaireSubmissionUrl = (accountId: string) => {
  return `/api/v2/accounts/${accountId}/latest-ip-regulatory-submission`;
};

export default function ToolRegulatoryQuestionnaireForm() {
  const { account } = useAuthentication();
  const methods = useForm({
    shouldUnregister: false,
  });
  const { setNotification } = useNotification();
  const [loading, setLoading] = useState(false);

  const {
    data: submission,
    isValidating: isValidatingSubmission,
    mutate,
  } = useSWR(
    account?.id ? regulatoryQuestionnaireSubmissionUrl(account?.id) : null,
    baseFetcher,
    {
      revalidateOnFocus: false,
    }
  );
  const { data: form, isValidating } = useSWR(
    regulatoryQuestionnaireFormUrl,
    baseFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const flattenedFields = useMemo(() => {
    if (!form) return;

    const result: any = [];

    form?.formFieldGroups?.forEach((group: any) => {
      (group.fields || []).forEach((field: any) => {
        result.push(field);

        (field.dependentFieldFilters || []).forEach((dep: any) => {
          const dependentField = dep.dependentFormField;
          if (dependentField) {
            dependentField.__dependsOn = {
              parentName: field.name,
              expectedValues: dep.filters?.[0]?.strValues || [],
              action: dep.formFieldAction,
            };
            result.push(dependentField);
          }
        });
      });
    });

    return result;
  }, [form]);

  const normalizeFormValues = useCallback(
    (values) => {
      if (!form) return;

      return values.reduce((acc: any, { name, value }: any) => {
        const field = flattenedFields.find((item: any) => item.name === name);

        if (acc[name]) {
          if (Array.isArray(acc[name])) {
            acc[name].push(value);
          } else {
            acc[name] = [acc[name], value];
          }
        } else {
          if (field.fieldType === "checkbox") {
            acc[name] = [value];
          } else {
            acc[name] = value;
          }
        }
        return acc;
      }, {});
    },
    [flattenedFields, form]
  );

  const onUsePreviousSubmission = useCallback(() => {
    if (!submission?.values) return;
    const normalizedValues = normalizeFormValues(submission?.values);

    Object.entries(normalizedValues).forEach(([key, value]) => {
      methods.setValue(key, value);
      methods.clearErrors();
    });
  }, [methods, normalizeFormValues, submission?.values]);

  const formatHubspotValue = useCallback((data) => {
    const values: any = [];
    Object.keys(data).forEach((key) => {
      if (!data[key]) return;

      if (isArray(data[key])) {
        return data[key].forEach((item: any) => {
          values.push({
            name: key,
            value: item,
            objectTypeId: "0-1",
          });
        });
      } else {
        values.push({
          name: key,
          value: data[key],
          objectTypeId: "0-1",
        });
      }
    });

    return values;
  }, []);

  const onSubmit = useCallback(
    async (data: any) => {
      Swal.fire({
        icon: "question",
        title: "Do you want to save the changes?",
        confirmButtonColor: "#24a9a7",
        showCancelButton: true,
        confirmButtonText: "Save",
      }).then(async (result: any) => {
        const confirmed = result.isConfirmed;
        if (!account?.id || !confirmed) return;

        const values = formatHubspotValue(data);
        setLoading(true);

        try {
          await fetchApi("/api/v2/ip-regulatory-submissions", {
            method: "POST",
            body: JSON.stringify({
              data: {
                attributes: {
                  values: values,
                  "hubspot-form-submitted-at": new Date().toString(),
                },
                relationships: {
                  account: { data: { type: "accounts", id: account?.id } },
                },
                type: "ip-regulatory-submissions",
              },
            }),
          });

          setNotification(
            "Success! Thanks for submitting the form.",
            "success"
          );
          mutate();
          methods.reset({});
          window.scrollTo({ top: 0 });
          setLoading(false);
        } catch (error) {
          setLoading(false);
          console.error(error);
        }
      });
    },
    [account?.id, formatHubspotValue, methods, mutate, setNotification]
  );

  const fields = useMemo(() => {
    return form?.formFieldGroups?.map((group: any, index: number) => (
      <ToolRegulatoryQuestionnaireFormFieldGroup
        group={group}
        key={`field-group-${index}`}
      />
    ));
  }, [form?.formFieldGroups]);

  if (isValidating || isValidatingSubmission) {
    return (
      <div className="p-3 bg-[#f7f7f7] rounded-md flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      {submission?.values && (
        <div className="flex flex-col md:flex-row md:items-center justify-between bg-smart-teal/10 p-8 rounded-md mb-8">
          <div className="">
            <h4 className="mb-0">Reuse Your Last Submission</h4>
            <p className="mb-0">
              Your previous form details are available. Auto-fill and update as
              needed.
            </p>

            {submission?.hubspotFormSubmittedAt && (
              <p className="mt-2 font-bold mb-0">
                Last Submission Date:{" "}
                {format(
                  new Date(submission?.hubspotFormSubmittedAt),
                  "dd MMM yyyy"
                )}
              </p>
            )}
          </div>
          <button
            onClick={onUsePreviousSubmission}
            className="outline-0 mt-4 md:mt-0 border-0 bg-smart-teal hover:bg-dark-teal p-2 px-8 text-white font-medium rounded-full"
          >
            Auto-Fill Now
          </button>
        </div>
      )}
      <div className="bg-[#f7f7f7] rounded-xl px-4 md:px-8 pb-8">
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {fields}
          <button
            disabled={loading}
            type="submit"
            className="outline-0 border-0 bg-smart-teal text-[16px] hover:bg-dark-teal p-2 px-8 text-white font-medium rounded-full mt-8"
          >
            {loading ? "submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </FormProvider>
  );
}
